import { useMemo, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@wms/shared';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { ApiError, apiFetch } from '../../lib/api';
import { useCan, useSession } from '../../lib/session';
import { Combobox, type ComboboxOption } from '../product/combobox';
import { SkeletonBlock, SkeletonRows } from '../skeleton';
import type {
  AlertDetail,
  AlertGroup,
  AlertListItem,
  AlertListResponse,
  AlertPriority,
  AlertRule,
  AlertRuleKind,
  AlertRuleScopeType,
} from './types';

const PAGE_SIZE = 50;
const GROUPS: readonly AlertGroup[] = ['needsAttention', 'mine', 'resolved', 'all'];
const PRIORITIES: readonly AlertPriority[] = ['LOW', 'NORMAL', 'HIGH', 'CRITICAL'];
const ENTITY_TYPES = [
  'PRODUCT',
  'BATCH',
  'STOCK_BALANCE',
  'LOCATION',
  'WAREHOUSE',
  'ORDER',
  'RESERVATION',
  'SUPPLIER',
  'STOCK_COUNT',
] as const;

function titleFor(t: (k: string, o?: Record<string, unknown>) => string, item: AlertListItem): string {
  if (item.titleText) return item.titleText;
  if (item.titleKey) return t(item.titleKey, item.titleParams ?? {});
  return item.subtype;
}

const PRIORITY_TONE: Record<AlertPriority, 'default' | 'info' | 'warn' | 'danger'> = {
  LOW: 'default',
  NORMAL: 'info',
  HIGH: 'warn',
  CRITICAL: 'danger',
};

function PriorityBadge({ priority }: { readonly priority: AlertPriority }): React.JSX.Element {
  const { t } = useTranslation();
  const tone = PRIORITY_TONE[priority];
  return (
    <span className={tone === 'default' ? 'badge' : `badge badge--${tone}`}>
      {t(`alerts.priority${priority}`)}
    </span>
  );
}

function ErrorLine({ error }: { readonly error: unknown }): React.JSX.Element | null {
  const { t } = useTranslation();
  if (!(error instanceof ApiError)) return null;
  return (
    <p className="field-error">
      {t(`errors:${error.code}`, { defaultValue: error.code, ...error.params })}
    </p>
  );
}

/**
 * 预警中心（M8，S11）。四个分组标签对应后端 `group` 参数语义——
 * needsAttention 恒常显示（§28.6：condition_state=OPEN，不受 ACK 影响）。
 * 动作按钮按来源分级：SYSTEM/CUSTOM_RULE 只有 ACK/SNOOZE（条件真正消失
 * 只能靠评估器 AUTO_RESOLVE，§28.6 红线）；MANUAL 额外有 RESOLVE/DISMISS。
 * DISMISS 一条 CRITICAL 的 MANUAL 预警需要 `alert.dismiss_critical`；
 * 处理别人指派的预警需要 `alert.manage_others`（后端强制，这里只做
 * UX 层面的按钮禁用，不是唯一防线）。
 */
export function AlertCenterPage(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { data: session } = useSession();
  const canManageRules = useCan(PERMISSIONS.ALERT_RULE_MANAGE);
  const canDismissCritical = useCan(PERMISSIONS.ALERT_DISMISS_CRITICAL);
  const canManageOthers = useCan(PERMISSIONS.ALERT_MANAGE_OTHERS);

  const [group, setGroup] = useState<AlertGroup>('needsAttention');
  // 通知铃铛的深链（S18："每条通知点击后跳到对应预警"）——?open=<alertId>
  // 只用来设置详情抽屉的初始打开状态，不影响列表本身按哪个分组过滤
  // （被通知的预警未必落在当前 needsAttention 分组里，比如已经被别人处理过）。
  const [searchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string | null>(searchParams.get('open'));
  const [showCreate, setShowCreate] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' });

  const { data, isPending, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['alerts', 'list', group],
    queryFn: ({ pageParam }) =>
      apiFetch<AlertListResponse>(
        `/alerts?group=${group}&limit=${PAGE_SIZE}${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ''}`,
      ),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const items = useMemo(() => (data?.pages ?? []).flatMap((p) => p.items), [data]);

  const invalidateList = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['alerts', 'list'] });
  };

  return (
    <div>
      <div className="row row--between">
        <h1 style={{ fontSize: 'var(--text-2xl)' }}>{t('nav.alerts')}</h1>
        <div className="row" style={{ gap: 'var(--space-2)' }}>
          {canManageRules && (
            <button type="button" className="btn btn--ghost" onClick={() => setShowRules(true)}>
              {t('alerts.manageRules')}
            </button>
          )}
          <button type="button" className="btn btn--primary" onClick={() => setShowCreate(true)}>
            {t('alerts.newAlert')}
          </button>
        </div>
      </div>

      <div className="row" style={{ gap: 'var(--space-2)', margin: 'var(--space-3) 0' }}>
        {GROUPS.map((g) => (
          <button
            key={g}
            type="button"
            className={group === g ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'}
            onClick={() => setGroup(g)}
          >
            {t(`alerts.group${g[0]!.toUpperCase()}${g.slice(1)}`)}
          </button>
        ))}
      </div>

      {isPending ? (
        <SkeletonRows />
      ) : items.length === 0 ? (
        <div className="empty-state">
          <p>{t('alerts.empty')}</p>
        </div>
      ) : (
        <div className="table" style={{ display: 'block' }}>
          <div
            className="row"
            style={{
              fontWeight: 600,
              padding: 'var(--space-2) 0',
              borderBottom: '1px solid var(--color-border)',
            }}
          >
            <span style={{ width: '90px' }}>{t('alerts.colPriority')}</span>
            <span style={{ flex: 2 }}>{t('alerts.colTitle')}</span>
            <span style={{ width: '140px' }}>{t('alerts.colSource')}</span>
            <span style={{ width: '140px' }}>{t('alerts.colStatus')}</span>
            <span style={{ width: '160px' }}>{t('alerts.colDetectedAt')}</span>
          </div>
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setSelectedId(item.id)}
              className="data-row"
              style={{ height: '48px' }}
            >
              <span style={{ width: '90px' }}>
                <PriorityBadge priority={item.priority} />
              </span>
              <span style={{ flex: 2 }}>
                {titleFor(t, item)}
                {item.conditionState === 'OPEN' && item.status !== 'ACTIVE' && (
                  <span className="field-hint" style={{ marginLeft: 'var(--space-2)' }}>
                    {t('alerts.stillOpenHint')}
                  </span>
                )}
              </span>
              <span style={{ width: '140px' }}>{t(`alerts.source${item.source}`)}</span>
              <span style={{ width: '140px' }}>
                <span
                  className={
                    item.status === 'RESOLVED'
                      ? 'badge badge--success'
                      : item.status === 'DISMISSED'
                        ? 'badge'
                        : item.status === 'ACKNOWLEDGED'
                          ? 'badge badge--info'
                          : item.status === 'SNOOZED'
                            ? 'badge'
                            : 'badge badge--danger'
                  }
                >
                  {t(`alerts.status${item.status}`)}
                </span>
              </span>
              <span style={{ width: '160px' }}>{fmt.format(new Date(item.firstDetectedAt))}</span>
            </button>
          ))}
          {hasNextPage && (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={isFetchingNextPage}
              onClick={() => void fetchNextPage()}
              style={{ margin: 'var(--space-3) 0' }}
            >
              {isFetchingNextPage ? t('app.loading') : t('app.loadMore')}
            </button>
          )}
        </div>
      )}

      {selectedId && (
        <AlertDetailDrawer
          alertId={selectedId}
          currentUserId={session?.userId ?? null}
          canDismissCritical={canDismissCritical}
          canManageOthers={canManageOthers}
          onClose={() => setSelectedId(null)}
          onChanged={invalidateList}
        />
      )}

      {showCreate && (
        <CreateManualAlertModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            invalidateList();
          }}
        />
      )}

      {showRules && canManageRules && <RulesModal onClose={() => setShowRules(false)} />}
    </div>
  );
}

// =============================================================================
// 详情抽屉：完整信息 + 处理历史 + 动作按钮
// =============================================================================
function AlertDetailDrawer({
  alertId,
  currentUserId,
  canDismissCritical,
  canManageOthers,
  onClose,
  onChanged,
}: {
  readonly alertId: string;
  readonly currentUserId: string | null;
  readonly canDismissCritical: boolean;
  readonly canManageOthers: boolean;
  readonly onClose: () => void;
  readonly onChanged: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [snoozeUntil, setSnoozeUntil] = useState('');
  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' });

  const { data: detail, isPending } = useQuery({
    queryKey: ['alerts', 'detail', alertId],
    queryFn: () => apiFetch<AlertDetail>(`/alerts/${alertId}`),
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['alerts', 'detail', alertId] });
    onChanged();
  };

  const ackMutation = useMutation({
    mutationFn: () => apiFetch(`/alerts/${alertId}/ack`, { method: 'POST', body: {} }),
    onSuccess: invalidate,
  });
  const snoozeMutation = useMutation({
    mutationFn: () =>
      apiFetch(`/alerts/${alertId}/snooze`, {
        method: 'POST',
        body: { until: new Date(snoozeUntil).toISOString() },
      }),
    onSuccess: () => {
      setSnoozeUntil('');
      invalidate();
    },
  });
  const resolveMutation = useMutation({
    mutationFn: () =>
      apiFetch(`/alerts/${alertId}/resolve`, { method: 'POST', body: note ? { note } : {} }),
    onSuccess: () => {
      setNote('');
      invalidate();
    },
  });
  const dismissMutation = useMutation({
    mutationFn: () =>
      apiFetch(`/alerts/${alertId}/dismiss`, { method: 'POST', body: note ? { note } : {} }),
    onSuccess: () => {
      setNote('');
      invalidate();
    },
  });
  const assignToMeMutation = useMutation({
    mutationFn: () =>
      apiFetch(`/alerts/${alertId}/assign`, {
        method: 'POST',
        body: { userId: currentUserId },
      }),
    onSuccess: invalidate,
  });
  const unassignMutation = useMutation({
    mutationFn: () => apiFetch(`/alerts/${alertId}/assign`, { method: 'POST', body: {} }),
    onSuccess: invalidate,
  });
  const [assignTarget, setAssignTarget] = useState<ComboboxOption | null>(null);
  const assignToUserMutation = useMutation({
    mutationFn: () => apiFetch(`/alerts/${alertId}/assign`, { method: 'POST', body: { userId: assignTarget!.id } }),
    onSuccess: () => {
      setAssignTarget(null);
      invalidate();
    },
  });

  const isMine = !detail?.assignedUserId || detail.assignedUserId === currentUserId;
  const canAct = isMine || canManageOthers;
  const isManual = detail?.source === 'MANUAL';
  const isOpen = detail?.conditionState === 'OPEN';
  const dismissBlocked = detail?.priority === 'CRITICAL' && !canDismissCritical;

  return (
    <div className="modal-overlay" role="presentation" onClick={onClose}>
      <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        {isPending || !detail ? (
          <SkeletonBlock height={160} />
        ) : (
          <>
            <div className="row row--between">
              <h2>{titleFor(t, detail)}</h2>
              <PriorityBadge priority={detail.priority} />
            </div>
            {detail.bodyText && <p>{detail.bodyText}</p>}
            <p className="field-hint">
              {t(`alerts.source${detail.source}`)} · {t(`alerts.entity${detail.entityType}`)} ·{' '}
              {t('alerts.detectedAtLabel')} {fmt.format(new Date(detail.firstDetectedAt))}
            </p>

            {/* §28.6：条件仍然存在时明确告知，不管 status 显示什么 */}
            {isOpen ? (
              <p className="badge badge--danger">{t('alerts.conditionStillOpen')}</p>
            ) : (
              <p className="badge badge--success">{t('alerts.conditionCleared')}</p>
            )}
            <p>
              <strong>{t('alerts.statusLabel')}:</strong> {t(`alerts.status${detail.status}`)}
            </p>
            {detail.acknowledgedAt && (
              <p className="field-hint">
                {t('alerts.ackedAt', { at: fmt.format(new Date(detail.acknowledgedAt)) })}
              </p>
            )}
            {detail.snoozedUntil && (
              <p className="field-hint">
                {t('alerts.snoozedUntilLabel', { at: fmt.format(new Date(detail.snoozedUntil)) })}
              </p>
            )}
            {detail.resolvedAt && (
              <p className="field-hint">
                {t('alerts.resolvedAt', {
                  at: fmt.format(new Date(detail.resolvedAt)),
                  resolution: detail.resolution ?? '',
                })}
              </p>
            )}

            <div className="row" style={{ gap: 'var(--space-2)', margin: 'var(--space-2) 0' }}>
              {detail.assignedUserId ? (
                <span className="field-hint">
                  {isMine ? t('alerts.assignedToMe') : t('alerts.assignedToOther')}
                </span>
              ) : (
                <span className="field-hint">{t('alerts.unassigned')}</span>
              )}
              {canAct && (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  disabled={assignToMeMutation.isPending || unassignMutation.isPending}
                  onClick={() =>
                    detail.assignedUserId ? unassignMutation.mutate() : assignToMeMutation.mutate()
                  }
                >
                  {detail.assignedUserId ? t('alerts.unassignButton') : t('alerts.assignToMeButton')}
                </button>
              )}
            </div>

            {/* 指派给别人（不是自己）需要 alert.manage_others——与后端 assign()
                的权限校验对齐。指派到角色目前只有 API 支持（见
                notification.helpers.ts 的 notifyRole），UI 上暂不提供角色选择器
                （V1 范围裁剪：常见场景是"指派给某个具体的人"，角色指派留给
                后续打磨）。 */}
            {canManageOthers && (
              <div className="row" style={{ gap: 'var(--space-2)', marginBottom: 'var(--space-2)', alignItems: 'flex-end' }}>
                <div style={{ maxWidth: '260px', flex: 1 }}>
                  <Combobox
                    value={assignTarget}
                    onChange={setAssignTarget}
                    fetchOptions={async (q) => {
                      const res = await apiFetch<{ items: { id: string; displayName: string }[] }>(
                        `/alerts/assignable-users?query=${encodeURIComponent(q)}`,
                      );
                      return res.items.map((u) => ({ id: u.id, label: u.displayName }));
                    }}
                    placeholder={t('alerts.assignToUserIdHint')}
                  />
                </div>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  disabled={assignToUserMutation.isPending || !assignTarget}
                  onClick={() => assignToUserMutation.mutate()}
                >
                  {t('alerts.assignToUserButton')}
                </button>
              </div>
            )}
            <ErrorLine error={assignToUserMutation.error} />

            <ErrorLine error={ackMutation.error} />
            <ErrorLine error={snoozeMutation.error} />
            <ErrorLine error={resolveMutation.error} />
            <ErrorLine error={dismissMutation.error} />

            {canAct && (
              <div className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                {detail.status !== 'ACKNOWLEDGED' && (
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    disabled={ackMutation.isPending}
                    onClick={() => ackMutation.mutate()}
                  >
                    {t('alerts.ackButton')}
                  </button>
                )}
                <label className="field" style={{ maxWidth: '220px' }}>
                  <input
                    type="datetime-local"
                    value={snoozeUntil}
                    onChange={(e) => setSnoozeUntil(e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  disabled={snoozeMutation.isPending || !snoozeUntil}
                  onClick={() => snoozeMutation.mutate()}
                >
                  {t('alerts.snoozeButton')}
                </button>
                {/* §28.6 红线：只有 MANUAL 来源可以被直接 RESOLVE/DISMISS——
                    SYSTEM/CUSTOM_RULE 的条件消失只能靠评估器 AUTO_RESOLVE。 */}
                {isManual && (
                  <>
                    <button
                      type="button"
                      className="btn btn--primary btn--sm"
                      disabled={resolveMutation.isPending}
                      onClick={() => resolveMutation.mutate()}
                    >
                      {t('alerts.resolveButton')}
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      disabled={dismissMutation.isPending || dismissBlocked}
                      title={dismissBlocked ? t('alerts.dismissCriticalBlocked') : undefined}
                      onClick={() => dismissMutation.mutate()}
                    >
                      {t('alerts.dismissButton')}
                    </button>
                  </>
                )}
              </div>
            )}
            {isManual && canAct && (
              <label className="field" style={{ marginTop: 'var(--space-2)' }}>
                <span>{t('alerts.noteLabel')}</span>
                <input value={note} onChange={(e) => setNote(e.target.value)} />
              </label>
            )}

            <h3 style={{ marginTop: 'var(--space-4)' }}>{t('alerts.historyTitle')}</h3>
            <div className="field-hint">
              {detail.events.length === 0
                ? t('alerts.noHistory')
                : detail.events.map((ev) => (
                    <div key={ev.id}>
                      {t(`alerts.event${ev.action}`)} · {fmt.format(new Date(ev.at))}
                      {ev.note ? ` · ${ev.note}` : ''}
                    </div>
                  ))}
            </div>

            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={onClose}>
                {t('app.close')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// 手工创建提醒（§28.2）
// =============================================================================
function CreateManualAlertModal({
  onClose,
  onCreated,
}: {
  readonly onClose: () => void;
  readonly onCreated: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [titleText, setTitleText] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [priority, setPriority] = useState<AlertPriority>('NORMAL');
  const [entityType, setEntityType] = useState<(typeof ENTITY_TYPES)[number]>('WAREHOUSE');
  const [entityId, setEntityId] = useState('');

  const createMutation = useMutation({
    mutationFn: () =>
      apiFetch(`/alerts`, {
        method: 'POST',
        body: { titleText, bodyText: bodyText || undefined, priority, entityType, entityId },
      }),
    onSuccess: onCreated,
  });

  return (
    <div className="modal-overlay" role="presentation" onClick={onClose}>
      <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <h2>{t('alerts.newAlert')}</h2>
        <label className="field">
          <span>{t('alerts.titleLabel')}</span>
          <input value={titleText} onChange={(e) => setTitleText(e.target.value)} />
        </label>
        <label className="field">
          <span>{t('alerts.bodyLabel')}</span>
          <input value={bodyText} onChange={(e) => setBodyText(e.target.value)} />
        </label>
        <label className="field">
          <span>{t('alerts.colPriority')}</span>
          <select value={priority} onChange={(e) => setPriority(e.target.value as AlertPriority)}>
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {t(`alerts.priority${p}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{t('alerts.entityTypeLabel')}</span>
          <select
            value={entityType}
            onChange={(e) => setEntityType(e.target.value as (typeof ENTITY_TYPES)[number])}
          >
            {ENTITY_TYPES.map((e) => (
              <option key={e} value={e}>
                {t(`alerts.entity${e}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{t('alerts.entityIdLabel')}</span>
          <input
            value={entityId}
            onChange={(e) => setEntityId(e.target.value)}
            placeholder={t('alerts.entityIdHint') ?? ''}
          />
        </label>
        <ErrorLine error={createMutation.error} />
        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            {t('app.cancel')}
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={createMutation.isPending || !titleText.trim() || !entityId.trim()}
            onClick={() => createMutation.mutate()}
          >
            {createMutation.isPending ? t('app.loading') : t('app.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// 规则管理（`alert.rule.manage`）——DB CHECK 是唯一真理源，这里只做形状校验
// =============================================================================
/** 每种规则类型允许的范围——与后端 RULE_SCOPES_BY_KIND / DB CHECK（0012）同一口径 */
const SCOPES_BY_KIND: Record<AlertRuleKind, readonly AlertRuleScopeType[]> = {
  EXPIRY: ['GLOBAL', 'CATEGORY', 'PRODUCT'],
  LOW_STOCK: ['GLOBAL', 'CATEGORY', 'PRODUCT'],
  NEGATIVE_STOCK: ['GLOBAL', 'WAREHOUSE'],
  CUSTOM: ['GLOBAL'],
};
/** CUSTOM 没有评估器，不提供创建入口 */
const CREATABLE_KINDS: readonly AlertRuleKind[] = ['EXPIRY', 'LOW_STOCK', 'NEGATIVE_STOCK'];

/** 已存规则的人话摘要（"提前 60 天"/"阈值 20"），代替裸 JSON */
function ruleConfigSummary(
  t: (k: string, o?: Record<string, unknown>) => string,
  rule: AlertRule,
): string | null {
  const config = (rule.config ?? {}) as Record<string, unknown>;
  if (rule.kind === 'EXPIRY' && typeof config.days === 'number') {
    return t('alerts.ruleSummaryExpiry', { days: config.days });
  }
  if (rule.kind === 'LOW_STOCK' && (typeof config.qtyBase === 'string' || typeof config.qtyBase === 'number')) {
    return t('alerts.ruleSummaryLowStock', { qty: config.qtyBase });
  }
  return null;
}

function RulesModal({ onClose }: { readonly onClose: () => void }): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<AlertRuleKind>('LOW_STOCK');
  const [scopeType, setScopeType] = useState<AlertRuleScopeType>('GLOBAL');
  const [scopeTarget, setScopeTarget] = useState<ComboboxOption | null>(null);
  const [scopeWarehouseId, setScopeWarehouseId] = useState('');
  const [priority, setPriority] = useState<AlertPriority>('NORMAL');
  // 【用户反馈】"设置预警不应该让用户写代码"——JSON 文本框换成按类型出字段
  const [expiryDays, setExpiryDays] = useState('30');
  const [lowStockQty, setLowStockQty] = useState('');

  const { data, isPending } = useQuery({
    queryKey: ['alerts', 'rules'],
    queryFn: () => apiFetch<{ items: AlertRule[] }>('/alerts/rules/list'),
  });
  const { data: warehousesData } = useQuery({
    queryKey: ['warehouses'],
    queryFn: () => apiFetch<{ items: { id: string; name: string }[] }>('/warehouses'),
    enabled: scopeType === 'WAREHOUSE',
  });

  // 规则变更后端会立即全量求值——预警列表/铃铛一并刷新，用户马上看到效果
  const invalidate = (): void => void queryClient.invalidateQueries({ queryKey: ['alerts'] });

  const allowedScopes = SCOPES_BY_KIND[kind];
  const changeKind = (next: AlertRuleKind): void => {
    setKind(next);
    setScopeTarget(null);
    if (!SCOPES_BY_KIND[next].includes(scopeType)) setScopeType('GLOBAL');
  };

  const scopeIdValue =
    scopeType === 'GLOBAL'
      ? undefined
      : scopeType === 'WAREHOUSE'
        ? scopeWarehouseId
        : scopeTarget?.id;
  const configValid =
    kind === 'EXPIRY'
      ? /^\d+$/.test(expiryDays.trim()) && Number(expiryDays) >= 1 && Number(expiryDays) <= 3650
      : kind === 'LOW_STOCK'
        ? /^\d+$/.test(lowStockQty.trim())
        : true;
  const canSave = configValid && (scopeType === 'GLOBAL' || Boolean(scopeIdValue));

  const createMutation = useMutation({
    mutationFn: () => {
      const config: Record<string, unknown> =
        kind === 'EXPIRY'
          ? { mode: 'FIXED_DAYS', days: Number(expiryDays.trim()) }
          : kind === 'LOW_STOCK'
            ? { qtyBase: lowStockQty.trim() }
            : {};
      return apiFetch('/alerts/rules', {
        method: 'POST',
        body: { kind, scopeType, scopeId: scopeIdValue, config, priority },
      });
    },
    onSuccess: () => {
      setScopeTarget(null);
      setLowStockQty('');
      invalidate();
    },
  });
  const toggleMutation = useMutation({
    mutationFn: (input: { id: string; active: boolean }) =>
      apiFetch(`/alerts/rules/${input.id}`, { method: 'PATCH', body: { active: input.active } }),
    onSuccess: invalidate,
  });
  const removeMutation = useMutation({
    mutationFn: (id: string) => apiFetch(`/alerts/rules/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });

  return (
    <div className="modal-overlay" role="presentation" onClick={onClose}>
      <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <h2>{t('alerts.manageRules')}</h2>

        {isPending ? (
          <SkeletonRows rows={4} rowHeight={36} bars={3} />
        ) : (
          <div style={{ maxHeight: '260px', overflow: 'auto', marginBottom: 'var(--space-3)' }}>
            {(data?.items ?? []).map((rule) => (
              <div
                key={rule.id}
                className="row row--between"
                style={{ padding: 'var(--space-2) 0', borderBottom: '1px solid var(--color-border)' }}
              >
                <span>
                  {t(`alerts.ruleKind${rule.kind}`)} · {t(`alerts.ruleScope${rule.scopeType}`)}
                  {rule.scopeId ? ` (${rule.scopeId.slice(0, 8)})` : ''} · {t(`alerts.priority${rule.priority}`)}
                  {(() => {
                    const summary = ruleConfigSummary(t, rule);
                    return summary ? ` · ${summary}` : '';
                  })()}
                  {!rule.active && ` · ${t('alerts.ruleInactive')}`}
                </span>
                <span className="row" style={{ gap: 'var(--space-2)' }}>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => toggleMutation.mutate({ id: rule.id, active: !rule.active })}
                  >
                    {rule.active ? t('alerts.ruleDeactivate') : t('alerts.ruleActivate')}
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => removeMutation.mutate(rule.id)}
                  >
                    {t('app.delete')}
                  </button>
                </span>
              </div>
            ))}
            {(data?.items ?? []).length === 0 && (
              <p className="field-hint">{t('alerts.noRulesYet')}</p>
            )}
          </div>
        )}

        <h3>{t('alerts.newRule')}</h3>
        <label className="field">
          <span>{t('alerts.ruleKindLabel')}</span>
          <select value={kind} onChange={(e) => changeKind(e.target.value as AlertRuleKind)}>
            {CREATABLE_KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`alerts.ruleKind${k}`)}
              </option>
            ))}
          </select>
        </label>

        {/* 类型专属条件字段——不再让用户手写 JSON */}
        {kind === 'EXPIRY' && (
          <label className="field">
            <span>{t('alerts.ruleDaysBefore')}</span>
            <input
              value={expiryDays}
              onChange={(e) => setExpiryDays(e.target.value)}
              inputMode="numeric"
              pattern="[1-9][0-9]*"
              placeholder="60"
            />
            <span className="field-hint">{t('alerts.ruleDaysBeforeHint')}</span>
          </label>
        )}
        {kind === 'LOW_STOCK' && (
          <label className="field">
            <span>{t('alerts.ruleThreshold')}</span>
            <input
              value={lowStockQty}
              onChange={(e) => setLowStockQty(e.target.value)}
              inputMode="numeric"
              pattern="[0-9]+"
              placeholder="20"
            />
            <span className="field-hint">{t('alerts.ruleThresholdHint')}</span>
          </label>
        )}

        <label className="field">
          <span>{t('alerts.ruleScopeLabel')}</span>
          <select
            value={scopeType}
            onChange={(e) => {
              setScopeType(e.target.value as AlertRuleScopeType);
              setScopeTarget(null);
            }}
          >
            {allowedScopes.map((s) => (
              <option key={s} value={s}>
                {t(`alerts.ruleScope${s}`)}
              </option>
            ))}
          </select>
        </label>
        {scopeType === 'PRODUCT' && (
          <div className="field">
            <span>{t('alerts.ruleScopePRODUCT')}</span>
            <Combobox
              value={scopeTarget}
              onChange={setScopeTarget}
              fetchOptions={async (q) => {
                const res = await apiFetch<{ items: { id: string; name: string; sku: string }[] }>(
                  `/products?query=${encodeURIComponent(q)}&limit=20`,
                );
                return res.items.map((p) => ({ id: p.id, label: `${p.name} (${p.sku})` }));
              }}
              placeholder={t('alerts.rulePickProduct')}
            />
          </div>
        )}
        {scopeType === 'CATEGORY' && (
          <div className="field">
            <span>{t('alerts.ruleScopeCATEGORY')}</span>
            <Combobox
              value={scopeTarget}
              onChange={setScopeTarget}
              fetchOptions={async (q) => {
                const res = await apiFetch<{ items: { id: string; name: string }[] }>(
                  `/product-categories?query=${encodeURIComponent(q)}`,
                );
                return res.items.map((c) => ({ id: c.id, label: c.name }));
              }}
              placeholder={t('products.pickCategory')}
            />
          </div>
        )}
        {scopeType === 'WAREHOUSE' && (
          <label className="field">
            <span>{t('alerts.ruleScopeWAREHOUSE')}</span>
            <select value={scopeWarehouseId} onChange={(e) => setScopeWarehouseId(e.target.value)}>
              <option value="">{t('alerts.rulePickWarehouse')}</option>
              {(warehousesData?.items ?? []).map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="field">
          <span>{t('alerts.colPriority')}</span>
          <select value={priority} onChange={(e) => setPriority(e.target.value as AlertPriority)}>
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {t(`alerts.priority${p}`)}
              </option>
            ))}
          </select>
        </label>
        <ErrorLine error={createMutation.error} />
        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            {t('app.close')}
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={createMutation.isPending || !canSave}
            onClick={() => createMutation.mutate()}
          >
            {createMutation.isPending ? t('app.loading') : t('app.save')}
          </button>
        </div>
      </div>
    </div>
  );
}
